import { on, qs } from '../../core/dom';
import type { NavModuleInit } from '../../core/types';

/**
 * Mobile-only FinderBot nav button.
 *
 * On desktop the assistant renders as a floating FAB (owned by the chatbot
 * component). On mobile the FAB is hidden via CSS and this button drives the
 * same drawer through the assistant's public API (`window.frankieAssistant`),
 * which is defined synchronously when the chatbot bundle evaluates. The button
 * is only rendered server-side when `chatbotEnabled()` is true, so we don't
 * gate on the API here — but we guard the call in case the bundle hasn't run.
 */
declare global {
  interface Window {
    frankieAssistant?: {
      open: () => void;
      close: () => void;
      toggle: () => void;
    };
  }
  interface WindowEventMap {
    'finder-assistant-drawer-state': CustomEvent<{ isOpen: boolean }>;
  }
}

const init: NavModuleInit = () => {
  const button = qs<HTMLButtonElement>('[data-gn-trigger="finderbot"]');
  if (!button) return;

  const offClick = on(button, 'click', () => {
    window.frankieAssistant?.toggle();
  });

  // Keep the button's expanded state in sync with the drawer. openDrawer/
  // closeDrawer dispatch this on `window` with `{ detail: { isOpen } }`. The
  // `on` helper only types HTMLElement events, so bind this one natively.
  const onDrawerState = (e: WindowEventMap['finder-assistant-drawer-state']): void => {
    button.setAttribute('aria-expanded', String(e.detail?.isOpen === true));
  };
  window.addEventListener('finder-assistant-drawer-state', onDrawerState);

  return {
    destroy() {
      offClick();
      window.removeEventListener('finder-assistant-drawer-state', onDrawerState);
    },
  };
};

export default init;
